import React, { useCallback, useContext, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormSetError } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import {
  convertFileUrlToFileName,
  renderDateString,
  zodKeys,
} from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useListApi } from "@/hooks/useListApi";
import { DynamicSelectorV2 } from "../DynamicSelectorV2";
import { FaPaperclip } from "react-icons/fa6";
import { ImCross } from "react-icons/im";
import { Note, notesApi } from "@/api/notes";
import { Source, sourcesApi } from "@/api/source";
import { CustomToolTip } from "../CustomToolTip";
import { useTranslation } from "react-i18next";
import { DateFormField } from "@/components/custom/DateFormField";
import { useOptimisticLocking } from "@/hooks/useOptimisticLocking";
import { ConflictResolutionDialog } from "@/components/ui/conflict-resolution-dialog";
import {
  withVersion,
  createOptimisticLockingToasts,
} from "@/lib/optimisticLockingHelpers";
import { noteFieldConfig } from "@/lib/fieldDiffUtils";

export function AddUpdateNote({
  open,
  setOpen,
  note,
  trigger,
  refresh,
  reviewCaseId,
  defaultSourceId,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  note?: Note;
  trigger?: JSX.Element;
  refresh?: () => Promise<void>;
  reviewCaseId: number;
  defaultSourceId?: number;
}) {
  const { t } = useTranslation();
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;
  const { loading, user } = contextValues;
  const { setLoading } = set;

  const toasts = createOptimisticLockingToasts(t("notes.title.note"), t);

  const {
    loading: sourceLoading,
    search: sourceSearch,
    loadNext: loadNextSources,
    list: sourceList,
    apiResult: sourceResult,
    pagination,
  } = useListApi<Source>({
    baseApiObject: sourcesApi,
    defaultParams: { case_id: reviewCaseId },
    enable: open,
    additionalListIds: [note?.source?.id?.toString()],
  });

  const FormSchema = z.object({
    source_id: z.string().nullable().optional(),
    title: z.string().min(1, t("common.statictext.field_required")),
    author: z.string().min(1, t("common.statictext.field_required")),
    date: z.date().nullable().optional(),
    private: z.boolean().default(false),
    description: z.string().min(1, t("common.statictext.field_required")),
    starting_page: z.string().nullable().optional(),
    ending_page: z.string().nullable().optional(),
    starting_bates_number: z.string().optional(),
    ending_bates_number: z.string().optional(),
    file: z.instanceof(File).or(z.string()).nullable().optional(),
  });

  const RefinedFormSchema = FormSchema.refine(
    (data) => {
      const startPage = data.starting_page || 0;
      const endPage = data.ending_page || 0;
      return +startPage <= +endPage;
    },
    {
      message: t("common.statictext.endpage_must_be_greater_than_startpage"),
      path: ["ending_page"], // specify the field that will be invalid
    },
  ).refine(
    (data) => {
      const startBatesNumber = data.starting_bates_number || 0;
      const endBatesNumber = data.ending_bates_number || 0;
      return startBatesNumber <= endBatesNumber;
    },
    {
      message: t("common.statictext.endbates_must_be_greater_than_startbates"),
      path: ["ending_bates_number"], // specify the field that will be invalid
    },
  );

  const form = useForm<z.infer<typeof RefinedFormSchema>>({
    resolver: zodResolver(RefinedFormSchema),
    defaultValues: {
      source_id: null,
      title: "",
      author: "",
      date: null,
      private: false,
      description: "",
      starting_page: null,
      ending_page: null,
      starting_bates_number: "",
      ending_bates_number: "",
      file: null,
    },
  });

  // Function to get current form data for conflict comparison
  const getCurrentFormData = useCallback(
    (version: number): Note => {
      const formData = form.getValues();
      return {
        id: note?.id,
        version: version,
        title: formData.title,
        author: formData.author,
        date: formData.date?.toLocaleDateString("sv") || "",
        private: formData.private || false,
        description: formData.description,
        starting_page: formData.starting_page
          ? parseInt(formData.starting_page)
          : null,
        ending_page: formData.ending_page
          ? parseInt(formData.ending_page)
          : null,
        starting_bates_number: formData.starting_bates_number,
        ending_bates_number: formData.ending_bates_number,
        file: formData.file,
        source_id: formData.source_id ? parseInt(formData.source_id) : 0,
      } as Note;
    },
    [form, note?.id],
  );

  // Optimistic locking hook
  const {
    currentVersion,
    updateVersion,
    updateWithLocking,
    conflictState,
    handleRefresh,
    handleForceOverwrite,
    clearConflict,
  } = useOptimisticLocking<Note>({
    initialVersion: note?.version || 0,
    resourceId: note?.id || 0,
    resourceName: t("notes.title.note"),
    apiRead: (id) =>
      notesApi.read({
        id: id.toString(),
        token: user?.access,
      }),
    apiUpdate: (id, data) =>
      notesApi.update({
        id: id.toString() + "/",
        data,
        token: user?.access,
        headers: {
          "content-type": "multipart/form-data",
        },
      }),
    onUpdateSuccess: () => {
      toast(toasts.updateSuccess);
    },
    onConflict: () => {
      toast(toasts.conflictDetected);
    },
    onUpdateError: (error) => {
      toast(toasts.error(error));
    },
    diffConfig: noteFieldConfig,
    localData: note ? getCurrentFormData(note.version) : undefined,
  });

  const errorHandler = useApiErrorHandler("AddUpdateNote", {
    setFormError: form.setError as UseFormSetError<Record<string, unknown>>,
  });

  async function onSubmit(data: z.infer<typeof RefinedFormSchema>) {
    try {
      setLoading?.(true);
      if (note && note.id) {
        // UPDATE with optimistic locking
        const result = await updateWithLocking(
          withVersion(
            {
              case_id: reviewCaseId,
              source_id: data.source_id ? parseInt(data.source_id) : undefined,
              title: data.title,
              author: data.author,
              date: data.date?.toLocaleDateString("sv"),
              private: data.private,
              description: data.description,
              starting_page: data.starting_page
                ? parseInt(data.starting_page)
                : null,
              ending_page: data.ending_page ? parseInt(data.ending_page) : null,
              starting_bates_number: data.starting_bates_number,
              ending_bates_number: data.ending_bates_number,
              ...(typeof data.file === "string" ? {} : { file: data.file }),
              updated_by: user?.user.id,
            },
            currentVersion,
          ),
        );

        if (result.conflict || !result.success) return;
      } else {
        await notesApi
          .create({
            url: "/",
            headers: {
              "content-type": "multipart/form-data",
            },
            data: {
              case_id: reviewCaseId,
              source_id: data.source_id ? parseInt(data.source_id) : undefined,
              title: data.title,
              author: data.author,
              date: data.date?.toLocaleDateString("sv"),
              private: data.private,
              description: data.description,
              starting_page: data.starting_page
                ? parseInt(data.starting_page)
                : null,
              ending_page: data.ending_page ? parseInt(data.ending_page) : null,
              starting_bates_number: data.starting_bates_number,
              ending_bates_number: data.ending_bates_number,
              ...(typeof data.file === "string" ? {} : { file: data.file }),
              created_by: user?.user.id,
            },
            token: user?.access,
          })
          .then(() => {
            toast({
              title: t("common.toast.added_successfully", {
                resource: t("notes.title.note"),
              }),
            });
            form.reset();
          });
      }
      await refresh?.();
      setOpen(false);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading?.(false);
    }
  }

  useEffect(() => {
    if (note) {
      updateVersion(note.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...note,
            date: note.date
              ? new Date(renderDateString(note.date, true))
              : note.date,
            source_id: note.source ? note.source.id.toString() : undefined,
            associated_note: undefined, // pop it out
            case: undefined, // pop it out
            user: undefined, // pop it out
            source: undefined, // pop it out
            case_id: reviewCaseId.toString(),
            starting_page: note.starting_page
              ? note.starting_page.toString()
              : null,
            ending_page: note.ending_page ? note.ending_page.toString() : null,
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note, reviewCaseId]);

  useEffect(() => {
    if (defaultSourceId !== undefined && open) {
      form.setValue("source_id", defaultSourceId.toString(), {
        shouldTouch: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSourceId, open]);

  const buttonAction = note?.id
    ? t("common.button.update")
    : t("common.button.add");

  // Conflict resolution handlers
  const handleRefreshNote = async () => {
    if (!note?.id) return;
    const refreshed = await handleRefresh();
    if (refreshed) {
      // Update form with refreshed data
      updateVersion(refreshed.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...refreshed,
            date: refreshed.date
              ? new Date(renderDateString(refreshed.date, true))
              : refreshed.date,
            source_id: refreshed.source
              ? refreshed.source.id.toString()
              : undefined,
            associated_note: undefined,
            case: undefined,
            user: undefined,
            source: undefined,
            case_id: reviewCaseId.toString(),
            starting_page: refreshed.starting_page
              ? refreshed.starting_page.toString()
              : null,
            ending_page: refreshed.ending_page
              ? refreshed.ending_page.toString()
              : null,
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
      toast(toasts.refreshSuccess);
    }
  };

  const handleForceOverwriteNote = async () => {
    if (!conflictState.latestVersion || !note?.id) return;
    const formData = form.getValues();
    const result = await handleForceOverwrite(
      withVersion(
        {
          case_id: reviewCaseId,
          source_id: formData.source_id
            ? parseInt(formData.source_id)
            : undefined,
          title: formData.title,
          author: formData.author,
          date: formData.date?.toLocaleDateString("sv"),
          private: formData.private,
          description: formData.description,
          starting_page: formData.starting_page
            ? parseInt(formData.starting_page)
            : null,
          ending_page: formData.ending_page
            ? parseInt(formData.ending_page)
            : null,
          starting_bates_number: formData.starting_bates_number,
          ending_bates_number: formData.ending_bates_number,
          ...(typeof formData.file === "string" ? {} : { file: formData.file }),
          updated_by: user?.user.id,
        },
        conflictState.latestVersion,
      ),
    );
    if (result.success) {
      toast(toasts.overwriteSuccess);
      await refresh?.();
      setOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {!!trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          className="w-full md:w-[500px] max-h-screen overflow-y-auto"
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full flex flex-col gap-8"
            >
              <DialogHeader>
                <DialogTitle>
                  {buttonAction} {t("notes.title.note")}
                </DialogTitle>
              </DialogHeader>

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.title")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("notes.placeholder.title")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="private"
                render={({ field }) => (
                  <div>
                    <FormItem className="flex flex-row items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">
                        {t("common.label.private")}
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="author"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.author")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("common.placeholder.author")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <DateFormField
                    control={form.control}
                    name="date"
                    label={t("common.label.date")}
                    description={t("notes.statictext.date")}
                    required={true}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.description")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("common.placeholder.description")}
                        className="resize-none"
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="source_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      * {t("common.label.source")}{" "}
                      {sourceLoading
                        ? t("common.button.loading_with_parens")
                        : ""}
                    </FormLabel>
                    <DynamicSelectorV2
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                      options={sourceList.map((item) => ({
                        label: item.title,
                        value: item.id.toString(),
                      }))}
                      className="selector"
                      onLoadNext={loadNextSources}
                      onSearch={(searchValue) => {
                        sourceSearch.set(searchValue);
                      }}
                      placeholder={t("notes.placeholder.source")}
                      totalCount={sourceResult?.count || 0}
                      pageSize={pagination.pageSize}
                      currentPage={pagination.page}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="starting_page"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.label.start_page")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("common.placeholder.start_page")}
                        {...field}
                        value={field.value || undefined}
                        type="number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ending_page"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.label.end_page")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("common.placeholder.end_page")}
                        {...field}
                        value={field.value || undefined}
                        type="number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="starting_bates_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("common.label.start_bates_number")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("common.placeholder.start_bates_number")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ending_bates_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.label.end_bates_number")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("common.placeholder.end_bates_number")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="file"
                render={({ field }) => {
                  let fileName = null;
                  if (typeof field.value === "string") {
                    fileName = convertFileUrlToFileName(field.value);
                  }
                  return (
                    <FormItem>
                      <FormLabel>{t("common.label.file")}</FormLabel>
                      {fileName && (
                        <div className="flex gap-2 items-center">
                          <FaPaperclip color="skyblue" />
                          <a
                            href={field.value as string}
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-primary break-all"
                          >
                            {fileName}
                          </a>
                          <CustomToolTip
                            trigger={
                              <ImCross
                                className="cursor-pointer"
                                onClick={() => {
                                  form.setValue("file", null);
                                }}
                                color="red"
                              />
                            }
                            tip={t("common.statictext.remove_file")}
                          />
                        </div>
                      )}
                      <FormControl>
                        <Input
                          type="file"
                          {...field}
                          value={undefined}
                          onChange={(event) =>
                            form.setValue(
                              "file",
                              event.target.files ? event.target.files[0] : null,
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        {t("common.statictext.upload_files")}
                        <p className="text-sm text-info">
                          {t("common.statictext.upload_files_detail")}
                        </p>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <DialogFooter>
                <Button disabled={loading} type="submit">
                  {loading ? t("common.button.loading") : buttonAction}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        open={conflictState.isOpen}
        onOpenChange={(open) => !open && clearConflict()}
        currentVersion={currentVersion}
        latestVersion={conflictState.latestVersion}
        resourceName={t("notes.title.note")}
        onRefresh={handleRefreshNote}
        onOverwrite={handleForceOverwriteNote}
        loading={loading}
        fieldDiffs={conflictState.fieldDiffs}
        fieldLabels={Object.fromEntries(
          Object.entries(noteFieldConfig.fields).map(([field, config]) => [
            field,
            t(config.label),
          ]),
        )}
      />
    </>
  );
}
